"use client";

// The other half of app/error.tsx: the boundary for an error thrown by the ROOT
// LAYOUT itself.
//
// ⚠️ IT REPLACES THE DOCUMENT, and therefore has to draw one. When the root
// layout is the thing that threw, Next cannot render inside it — so this
// component substitutes for it entirely, <html> and <body> included, and it
// imports globals.css because the layout that normally does is gone. Without
// the tags it renders nothing at all, which is the failure mode this file is
// supposed to prevent.
//
// So it carries no sidebar and no topbar — none of that survives a layout that
// cannot render — and it deliberately duplicates a little of error.tsx rather
// than sharing a component with it. A shared component is one more module that
// could be the thing that just failed to load.
//
// The layout reads the session and the roster cache on every request, and the
// whole store dump on every document load, so it is not a hypothetical place
// for an exception to come from — it is the layer with the most to go wrong in
// it.

import { useEffect, useState } from "react";
import { reportClientError } from "@/lib/client-error-report";
import { reloadIfStale } from "@/lib/stale-build";
import { resetAndGoHome } from "@/lib/client-reset";
import "./globals.css";

// `reset` is passed by Next and deliberately not offered — one button, for the
// reason in app/error.tsx's header. Kept in the type so the omission reads as a
// decision rather than an oversight.
export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  // The reporter is a plain module, not a component, precisely so it still works
  // here — where the layout, and therefore <ClientErrorReporter />, is gone.
  const [ref, setRef] = useState<string | null>(null);
  const [fixing, setFixing] = useState(false);

  useEffect(() => {
    console.error("[ironside] root layout exception", { digest: error.digest, message: error.message, stack: error.stack });
    // eslint-disable-next-line react-hooks/set-state-in-effect -- the reference is the one this effect's report sent; it can't exist before the effect runs
    setRef(reportClientError({ error, source: "global-render", digest: error.digest }));
    // Same recovery as app/error.tsx: a page from a replaced build reloads once.
    // The meta tag it reads is gone with the layout, so pageBuildId() finds
    // nothing and this is a no-op here — kept because the day the layout renders
    // that tag from somewhere else, this is the line that would otherwise have
    // been forgotten.
    void reloadIfStale();
  }, [error]);

  return (
    <html lang="en">
      <body className="bg-app text-fg antialiased min-h-screen">
        <main className="mx-auto max-w-2xl px-6 py-20">
          <h1 className="text-[15px] font-semibold">The app failed to load.</h1>
          <p className="mt-2 text-[13px] text-text-muted">
            This one is ours, not yours, and nothing you have saved is affected. We have already
            been told; press the button below and you should be straight back to work.
          </p>

          {ref && (
            <div className="mt-4 rounded-lg border border-line bg-app px-4 py-3">
              <div className="text-[10px] font-mono uppercase tracking-widest font-semibold text-text-dim">
                Reference — send us this, or a photo of it
              </div>
              <div data-testid="global-error-ref" className="mt-1 font-mono text-[22px] font-bold tracking-widest text-fg select-all">
                {ref}
              </div>
            </div>
          )}

          {/* THE SAME SINGLE CONTROL as app/error.tsx — duplicated markup
              rather than a shared component, for the reason in this file's
              header: a component imported from elsewhere is one more module
              that could be the thing that just failed to load. The RESET LOGIC
              is shared, because lib/client-reset.ts is a plain module with no
              React in it, which is the same bargain lib/client-error-report.ts
              is already here on.

              It matters more here than there, if anything: this boundary fires
              when the ROOT LAYOUT threw, and the layout is the layer that reads
              the persona lens cookie. A lens pointing at somebody who no longer
              resolves would break every page on every load, and clearing it is
              precisely what this button does. */}
          <div className="mt-5">
            <button
              onClick={() => {
                setFixing(true);
                void resetAndGoHome();
              }}
              disabled={fixing}
              data-testid="global-error-reset-home"
              className="px-4 py-2.5 rounded-lg text-[13px] font-semibold text-onfill bg-accent-strong transition-colors disabled:opacity-70"
            >
              {fixing ? "Taking you back…" : "Take me back to my work"}
            </button>
            <p className="mt-2 text-[12px] text-text-dim">
              Clears this browser&apos;s saved app data and reloads your home page. You stay signed
              in, and nothing you have saved is affected.
            </p>
          </div>

          <pre
            data-testid="global-error-detail"
            className="mt-5 max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-md border border-hairline bg-app p-3 text-[11px] leading-relaxed text-text-muted select-all"
          >
            {[
              error.digest ? `digest: ${error.digest}` : null,
              error.message ? `message: ${error.message}` : null,
              error.stack ?? null,
            ]
              .filter(Boolean)
              .join("\n")}
          </pre>
        </main>
      </body>
    </html>
  );
}
